import { useEffect, useRef } from 'react';
import { Icon, type IconName } from './Icon';

export type ConfirmKind = 'destructive' | 'warning' | 'info' | 'gold';

export interface ConfirmRequest {
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  kind?: ConfirmKind;
  icon?: IconName;
}

interface Props {
  request: ConfirmRequest | null;
  onResolve: (ok: boolean) => void;
}

const KIND_ICON: Record<ConfirmKind, IconName> = {
  destructive: 'trash',
  warning:     'help',
  info:        'help',
  gold:        'sparkle',
};

export function ConfirmDialog({ request, onResolve }: Props) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!request) return;
    confirmBtnRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onResolve(false);
      if (e.key === 'Enter' && document.activeElement === confirmBtnRef.current) onResolve(true);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, onResolve]);

  if (!request) return null;

  const kind: ConfirmKind = request.kind || 'gold';
  const icon = request.icon || KIND_ICON[kind];

  return (
    <div className="confirm-root" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="confirm-backdrop" onClick={() => onResolve(false)} />
      <div className={`confirm-card ${kind}`}>
        <div className="confirm-icon-wrap">
          <span className="confirm-icon">
            <Icon name={icon} size={26} variant={kind === 'destructive' ? 'red' : kind === 'warning' ? 'amber' : kind === 'info' ? 'silver' : 'gold'} />
          </span>
        </div>

        <h3 id="confirm-title">{request.title}</h3>
        <p>{request.body}</p>

        <div className="confirm-actions">
          <button
            type="button"
            className="confirm-btn cancel"
            onClick={() => onResolve(false)}
          >
            {request.cancelLabel || 'Cancel'}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className={`confirm-btn primary ${kind}`}
            onClick={() => onResolve(true)}
          >
            {request.confirmLabel || 'Confirm'}
            <Icon name="arrowRight" size={14} variant={kind === 'destructive' ? 'red' : 'silver'} />
          </button>
        </div>

        <span className="confirm-hint">Press <code>Enter</code> to confirm · <code>Esc</code> to cancel</span>
      </div>
    </div>
  );
}
